import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useToast } from "@astrum/ui";
import type { ChannelMessages } from "./useChannelMessages.js";

const MAX_JUMP_PAGES = 5;

/** How long the "is-jumped" flash runs, matching its CSS animation. */
const FLASH_MS = 2000;

/**
 * Scrolling to one message and flashing it: for an in-channel search result
 * (`jumpTo`), and for a `?message=` deep link from a notification or a
 * cross-channel MessageHit, which may have to page back to find it.
 */
export function useMessageJump(
  channelId: string | undefined,
  thread: Pick<ChannelMessages, "messages" | "loading" | "loadedChannelId" | "loadingOlder" | "hasMoreOlder" | "loadOlder">,
): { jumpedId: string | null; jumpTo: (messageId: string) => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  /** Set when a search result is clicked; cleared once the flash has run. */
  const [jumpedId, setJumpedId] = useState<string | null>(null);
  const { messages, loading: messagesLoading, loadedChannelId, loadingOlder, hasMoreOlder, loadOlder } = thread;

  const flash = useCallback((messageId: string) => {
    setJumpedId(messageId);
    // Matches the flash animation, so the class is not left on a message that
    // stopped being highlighted a minute ago.
    setTimeout(() => setJumpedId((current) => (current === messageId ? null : current)), FLASH_MS);
  }, []);

  /** In-channel search: results are assumed to be within the loaded window. */
  const jumpTo = useCallback(
    (messageId: string) => {
      const node = document.getElementById(`message-${messageId}`);
      if (!node) {
        // Older than what is loaded. Saying so beats scrolling
        // nowhere and looking broken.
        toast("That message is further back than this thread has loaded.", { tone: "danger" });
        return;
      }
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      flash(messageId);
    },
    [flash, toast],
  );

  /*
   * Jump to a message named by `?message=` — the destination a notification
   * or a cross-channel MessageHit deep-links to.
   *
   * The same jump-and-highlight as jumpTo above. The one thing jumpTo does
   * NOT do — because in-channel search results are assumed to already be
   * within the loaded window — is walk pagination backwards to find a
   * message that predates what's loaded. This does that: if the message
   * isn't on the page yet, ask loadOlder for another page and check again,
   * capped so a message that was deleted (or never existed) doesn't spin
   * forever.
   *
   * The param is consumed once the jump succeeds or gives up, and then
   * stripped from the URL — a refresh should show the channel as it is now,
   * not re-attempt the same jump.
   */
  const jumpPageAttempts = useRef(0);
  useEffect(() => {
    const targetId = searchParams.get("message");
    if (!targetId || !channelId || messagesLoading) return;
    // Opening a channel from a notification runs this in the same commit as
    // the channel switch, before the load effect's state lands — `messages`,
    // `messagesLoading` and `hasMoreOlder` still describe the previous
    // channel, or nothing at all on a fresh mount (no messages, not loading,
    // no older pages — which read as "not here, nothing more to fetch" and
    // gave up on every deep link). Wait for this channel's own first page
    // rather than paging (or giving up) against the wrong one.
    if (loadedChannelId !== channelId) return;

    const clearParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("message");
          return next;
        },
        { replace: true },
      );
    };

    const node = document.getElementById(`message-${targetId}`);
    if (node) {
      jumpPageAttempts.current = 0;
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      flash(targetId);
      clearParam();
      return;
    }

    // Not on the loaded page yet. Ask for one more page of history, unless
    // we've already tried enough of them or there simply isn't any more.
    if (loadingOlder) return;
    if (!hasMoreOlder || jumpPageAttempts.current >= MAX_JUMP_PAGES) {
      // Give up gracefully: stay at the channel's current scroll position
      // rather than forcing a broken jump, same tone as the in-channel
      // search's "further back than this thread has loaded" toast.
      toast("That message is further back than this thread has loaded.", { tone: "danger" });
      jumpPageAttempts.current = 0;
      clearParam();
      return;
    }
    jumpPageAttempts.current += 1;
    loadOlder();
    // Re-runs as `messages`/`loadingOlder`/`hasMoreOlder` change so each
    // successive older page gets checked in turn.
  }, [searchParams, channelId, messagesLoading, loadedChannelId, messages, loadingOlder, hasMoreOlder, loadOlder, setSearchParams, toast, flash]);

  return { jumpedId, jumpTo };
}
